import { Component, OnInit, HostListener } from "@angular/core";
import { CommonModule } from "@angular/common";
import { RouterOutlet } from "@angular/router";
import { NgxPaginationModule } from "ngx-pagination";
import { Chart, registerables } from "chart.js";
import { NgApexchartsModule } from "ng-apexcharts";
import { PullToRefreshService } from "./shared/services/pull-to-refresh.service";
import { Router } from "@angular/router";
import { trigger, state, style, transition, animate } from "@angular/animations";

Chart.register(...registerables);

@Component({
  selector: "app-root",
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    NgxPaginationModule,
    NgApexchartsModule,
  ],
  templateUrl: "./app.component.html",
  styleUrl: "./app.component.css",
  animations: [
    trigger('fadeInOut', [
      state('in', style({ opacity: 1, transform: 'translateY(0)' })),
      transition('void => *', [
        style({ opacity: 0, transform: 'translateY(-20px)' }),
        animate('300ms ease-in')
      ]),
      transition('* => void', [
        animate('300ms ease-out', style({ opacity: 0, transform: 'translateY(20px)' }))
      ])
    ]),
    trigger('pullIndicator', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-100%)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      transition(':leave', [
        animate('300ms ease-in', style({ opacity: 0, transform: 'translateY(-100%)' }))
      ])
    ])
  ]
})
export class AppComponent implements OnInit {
  title = "Prime Backoffice";
  isMobile = false;
  isRefreshing = false;
  isPulling = false;
  pullDistance = 0;
  refreshProgress = 0;

  private startY = 0;
  private currentY = 0;
  private pullThreshold = 200; // Distance minimale pour déclencher le refresh
  private maxPullDistance = 300; // Distance maximale de l'indicateur

  constructor(
    private pullToRefreshService: PullToRefreshService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.isMobile = this.detectTouchDevice();
    this.pullToRefreshService.registerRefreshCallback('global', () => this.performGlobalRefresh());
  }

  /**
   * Handle touch start for pull-to-refresh
   */
  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent): void {
    if (!this.isMobile || this.isRefreshing) return;

    const isAtTop = window.scrollY === 0;
    if (isAtTop) {
      this.startY = event.touches[0].clientY;
      this.isPulling = false;
    }
  }

  /**
   * Handle touch move for pull-to-refresh
   */
  @HostListener('touchmove', ['$event'])
  onTouchMove(event: TouchEvent): void {
    if (!this.isMobile || this.isRefreshing || this.startY === 0) return;

    this.currentY = event.touches[0].clientY;
    const distance = this.currentY - this.startY;

    // Only track pull down gestures when at top
    const isAtTop = window.scrollY === 0;

    if (isAtTop && distance > 0) {
      // Appliquer une résistance progressive (effet élastique)
      this.pullDistance = Math.min(
        this.maxPullDistance,
        distance * 0.5 // Facteur de résistance
      );

      this.isPulling = this.pullDistance > 10;

      // Empêcher le scroll par défaut si on tire
      if (this.isPulling) {
        event.preventDefault();
      }
    }
  }

  /**
   * Handle touch end
   */
  @HostListener('touchend', ['$event'])
  onTouchEnd(event: TouchEvent): void {
    if (!this.isMobile || this.isRefreshing) return;

    // Déclencher le refresh si on a dépassé le seuil
    if (this.pullDistance >= this.pullThreshold) {
      this.triggerGlobalRefresh();
    } else {
      // Reset l'indicateur avec animation
      this.isPulling = false;
      this.pullDistance = 0;
    }

    this.startY = 0;
  }

  /**
   * Détection améliorée des appareils tactiles
   */
  private detectTouchDevice(): boolean {
    const hasTouchScreen = (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      (navigator as any).msMaxTouchPoints > 0
    );

    const isSmallScreen = window.innerWidth <= 1024;

    return hasTouchScreen && isSmallScreen;
  }

  /**
   * Calculer la progression visuelle (0-100%)
   */
  get pullProgress(): number {
    return Math.min(100, (this.pullDistance / this.pullThreshold) * 100);
  }

  /**
   * Vérifier si on doit afficher "Relâchez pour actualiser"
   */
  get canRelease(): boolean {
    return this.pullDistance >= this.pullThreshold;
  }

  /**
   * Trigger global refresh from pull gesture
   */
  private async triggerGlobalRefresh(): Promise<void> {
    if (this.isRefreshing) return;

    this.isRefreshing = true;
    this.isPulling = false;
    this.pullDistance = 0;
    this.refreshProgress = 0;

    // Animation de progression
    const progressInterval = setInterval(() => {
      this.refreshProgress += 10;
      if (this.refreshProgress >= 90) {
        clearInterval(progressInterval);
      }
    }, 50);

    try {
      await this.pullToRefreshService.refresh('global', { key: 'global', forceRefresh: true });
      this.refreshProgress = 100;

      // Attendre un peu pour l'animation
      setTimeout(() => {
        this.isRefreshing = false;
        this.refreshProgress = 0;
      }, 400);
    } catch (error) {
      console.error('Global refresh failed:', error);
      this.isRefreshing = false;
      this.refreshProgress = 0;
    }

    clearInterval(progressInterval);
  }

  /**
   * Legacy method for backward compatibility
   */
  onRefresh(event?: any): void {
    this.triggerGlobalRefresh().then(() => {
      if (event && event.complete) {
        event.complete();
      }
    });
  }

  /**
   * Perform global refresh logic
   */
  private async performGlobalRefresh(): Promise<void> {
    this.pullToRefreshService.clearCache();

    const currentUrl = this.router.url;
    await this.router.navigateByUrl('/', { skipLocationChange: true });
    await this.router.navigate([currentUrl]);
  }
}
